import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import api from '../api/axiosInstance';
import { PermissionManager } from '../utils/PermissionManager';
import { BoardPost, BoardListResponse } from './types';
import './css/board.css';

const BoardList: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [searchType, setSearchType] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  const pageSize = 10;

  // 게시글 목록 불러오기
  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const response = await api.get<BoardListResponse>('/api/board', {
          params: {
            searchType,
            keyword,
            page: currentPage,
            size: pageSize
          }
        });

        setPosts(response.data.content);
        setTotalPages(response.data.totalPages);
        setTotalElements(response.data.totalElements);
        console.log('Pagination info:', {
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements,
          currentPage: response.data.number || currentPage,
          pageSize: response.data.size || pageSize
        });
      } catch (error) {
        console.error('Failed to fetch posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, [currentPage, searchType, keyword]);

  const handleSearch = () => {
    setCurrentPage(0);
    // currentPage 변경으로 useEffect가 자동 실행됨
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\. /g, '-').replace(/\./g, '');
  };

  const goToPage = (page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPagination = () => {
    const pages: React.ReactElement[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`page-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => goToPage(i)}
        >
          {i + 1}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          className="page-btn"
          onClick={() => goToPage(0)}
          disabled={currentPage === 0}
        >
          처음
        </button>
        <button
          className="page-btn"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 0}
        >
          이전
        </button>
        {pages}
        <button
          className="page-btn"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
        >
          다음
        </button>
        <button
          className="page-btn"
          onClick={() => goToPage(totalPages - 1)}
          disabled={currentPage >= totalPages - 1}
        >
          마지막
        </button>
      </div>
    );
  };

  const canCreatePost = () => {
    return PermissionManager.canCreateBoardPost();
  };

  return (
    <div className="board-container">
      <header className="board-header">
        <h1 className="board-title">게시판</h1>
        <div className="board-nav">
          <button
            className="nav-btn"
            onClick={() => navigate('/estimateForm')}
          >
            계산기 페이지
          </button>
          {/* {PermissionManager.canAccessAdminPage() && (
            <button
              className="nav-btn"
              onClick={() => navigate('/admin')}
            >
              관리자 페이지
            </button>
          )} */}
        </div>
      </header>

      <div className="board-content">
        <div className="board-controls">
          <div className="search-section">
            <select 
              value={searchType} 
              onChange={(e) => setSearchType(e.target.value)}
              className="search-select"
            >
              <option value="all">전체</option>
              <option value="title">제목</option>
              <option value="author">작성자</option>
            </select>
            <div className="search-input-container">
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="검색어를 입력하세요"
                className="search-input"
              />
              <button onClick={handleSearch} className="search-btn">
                <Search size={18} />
              </button>
            </div>
          </div>

          {canCreatePost() && (
            <button 
              className="create-btn"
              onClick={() => navigate('/board/write')}
            >
              <Plus size={18} />
              글 작성
            </button>
          )}
        </div>

        <div className="board-stats">
          전체 {totalElements}개의 게시글
        </div>

        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : (
          <>
            <div className="board-table">
              <div className="table-header">
                <div className="col-no">번호</div>
                <div className="col-title">제목</div>
                <div className="col-author">작성자</div>
                <div className="col-date">작성일</div>
                <div className="col-views">조회</div>
              </div>
              
              {posts.length === 0 ? (
                <div className="no-posts">게시글이 없습니다.</div>
              ) : (
                posts.map((post, index) => (
                  <div 
                    key={post.id} 
                    className="table-row"
                    onClick={() => navigate(`/board/${post.id}`)}
                  >
                    <div className="col-no">
                      {totalElements - (currentPage * pageSize) - index}
                    </div>
                    <div className="col-title">
                      <span className="title-text">
                        {post.title}
                        {post.files && post.files.length > 0 && (
                          <span className="file-icon">📎</span>
                        )}
                      </span>
                    </div>
                    <div className="col-author">{post.author}</div>
                    <div className="col-date">
                      {formatDate(post.createdDate)}
                    </div>
                    <div className="col-views">
                      {post.viewCount}
                    </div>
                  </div>
                ))
              )}
            </div>

            {totalPages > 0 && renderPagination()}
          </>
        )}
      </div>
    </div>
  );
};

export default BoardList;